import React, { ReactNode, useEffect, useState, createContext, useContext } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { buildUnauthorizedUrl, isDevLoginSessionLossCode, signedFetch } from '../../super-picker/services/api';
import { fetchPipelineAccess } from '../services/api';

export type ProjectDeckAccessState = 'checking' | 'granted' | 'denied';
export type ProjectDeckSession = { Name?: string; Photo?: string };
const ProjectDeckSessionContext = createContext<ProjectDeckSession | null>(null);

export function useProjectDeckSession(): ProjectDeckSession | null {
  return useContext(ProjectDeckSessionContext);
}

type Props = {
  children: ReactNode;
};

function AccessLoading() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
      <CircularProgress />
      <Typography color="text.secondary">Checking Project Deck access…</Typography>
    </Box>
  );
}

/** Shared authentication and Project Deck ADMIN gate for every deck route. */
export function ProjectDeckAccessGate({ children }: Props) {
  const [state, setState] = useState<ProjectDeckAccessState>('checking');
  const [session, setSession] = useState<ProjectDeckSession | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await signedFetch('/api/users/me');
        const data = await response.json().catch(() => ({})) as { code?: string; user?: { Name?: string; name?: string; Photo?: string; photo?: string } };
        if (!response.ok) {
          if (mounted) setState('denied');
          if (!(response.status === 401 && isDevLoginSessionLossCode(data.code))) {
            window.location.replace(buildUnauthorizedUrl(data.code, { app: 'super-picker' }));
          }
          return;
        }
        if (data.user) setSession({ Name: data.user.Name ?? data.user.name, Photo: data.user.Photo ?? data.user.photo });
        const allowed = await fetchPipelineAccess().catch(() => false);
        if (!allowed) {
          if (mounted) setState('denied');
          window.location.replace(buildUnauthorizedUrl('NO_PROJECT_DECK_ACCESS', { app: 'super-picker' }));
          return;
        }
        if (mounted) setState('granted');
      } catch {
        if (mounted) setState('denied');
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (state === 'checking') return <AccessLoading />;
  if (state === 'denied') return null;
  return <ProjectDeckSessionContext.Provider value={session}>{children}</ProjectDeckSessionContext.Provider>;
}
